import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import FileUploadField from "../components/FileUploadField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  MessageSquare,
  User,
  Folder,
  AlertTriangle,
  UserCheck,
  History,
  Filter,
  Brain,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ArtDirectorReviewPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [revisionComment, setRevisionComment] = useState("");
  const [revisionAttachment, setRevisionAttachment] = useState("");
  const [processing, setProcessing] = useState(false);
  const [artDirectors, setArtDirectors] = useState([]);
  const [activeTab, setActiveTab] = useState("queue"); // "queue" | "history" | "qc_blocked"
  const [reviewHistory, setReviewHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [adFilter, setAdFilter] = useState("all");
  const [expandedQcTaskId, setExpandedQcTaskId] = useState(null);
  const [qcBlocked, setQcBlocked] = useState([]);
  const [qcBlockedLoading, setQcBlockedLoading] = useState(false);
  const [qcMode, setQcMode] = useState("off");

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get("/art-director/review-queue");
      setTasks(response.data || []);
    } catch (error) {
      console.error("Error fetching review queue:", error);
      toast.error("Failed to load review tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchArtDirectors = async () => {
    try {
      const response = await api.get("/users?role=art_director");
      setArtDirectors(response.data || []);
    } catch (error) {
      console.error("Error fetching art directors:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get("/art-director/review-history");
      setReviewHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching review history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchQcBlocked = async () => {
    try {
      setQcBlockedLoading(true);
      const res = await api.get("/art-director/qc-blocked-queue");
      setQcBlocked(res.data || []);
    } catch {
      // non-fatal
    } finally {
      setQcBlockedLoading(false);
    }
  };

  const fetchQcMode = async () => {
    try {
      const res = await api.get("/settings/integrations");
      setQcMode(res.data?.ai_qc_mode || "off");
    } catch {
      // non-fatal — non-admin may get 403, in which case we don't render the tab
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchArtDirectors();
    fetchQcBlocked();
    fetchQcMode();
  }, []);

  // Lazy load history when tab is first opened
  useEffect(() => {
    if (activeTab === "history" && reviewHistory.length === 0 && !historyLoading) {
      fetchHistory();
    }
  }, [activeTab]);

  // Filtered history by art director
  const filteredHistory = useMemo(() => {
    if (adFilter === "all") return reviewHistory;
    return reviewHistory.filter(r => r.art_director_id === adFilter);
  }, [reviewHistory, adFilter]);

  // Unique art directors from history
  const historyADs = useMemo(() => {
    const adMap = new Map();
    reviewHistory.forEach(r => {
      if (r.art_director_id && r.art_director_name) {
        adMap.set(r.art_director_id, r.art_director_name);
      }
    });
    return Array.from(adMap.entries()).map(([id, name]) => ({ id, name }));
  }, [reviewHistory]);

  const handleAssignArtDirector = async (taskId, artDirectorId) => {
    try {
      await api.put(`/tasks/${taskId}`, { art_director_id: artDirectorId, art_director_review: true });
      toast.success("Art director assigned");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to assign art director");
    }
  };

  const handleApprove = async (taskId) => {
    try {
      setProcessing(true);
      await api.post(`/art-director/tasks/${taskId}/approve`);
      toast.success("Approved — sent to client review");
      fetchTasks();
      fetchQcBlocked();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleOverrideQcBlocked = async (run_id) => {
    try {
      await api.post(`/ai-qc/runs/${run_id}/override`, { unblock_to_ad: true });
      toast.success("Version returned to AD queue");
      fetchQcBlocked();
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Override failed");
    }
  };

  const handleOpenRevisionDialog = (task) => {
    setSelectedTask(task);
    setRevisionComment("");
    setRevisionDialogOpen(true);
  };

  const handleRequestRevision = async () => {
    if (!selectedTask) return;
    
    try {
      setProcessing(true);
      // Build query params - comment and optional attachment
      let queryParams = `comment=${encodeURIComponent(revisionComment)}`;
      if (revisionAttachment) {
        queryParams += `&attachment=${encodeURIComponent(revisionAttachment)}`;
      }
      await api.post(`/art-director/tasks/${selectedTask.id}/revision?${queryParams}`);
      toast.success("Revision requested — sent back to artist");
      setRevisionDialogOpen(false);
      setSelectedTask(null);
      setRevisionComment("");
      setRevisionAttachment("");
      fetchTasks();
      fetchQcBlocked();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request revision");
    } finally {
      setProcessing(false);
    }
  };

  const getLatestVersion = (task) => {
    if (!task.versions || task.versions.length === 0) return null;
    return task.versions[task.versions.length - 1];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Eye className="h-7 w-7 text-purple-400" />
              Tasks for Review
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Tasks awaiting Art Director approval. Approve sends to client review; revision sends back to artist.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-zinc-800/50 rounded-lg p-0.5">
              <button
                onClick={() => setActiveTab("queue")}
                className={cn("px-4 py-2 rounded-md text-sm font-medium transition-all",
                  activeTab === "queue" ? "bg-purple-500/20 text-purple-400" : "text-zinc-500 hover:text-zinc-300"
                )}
                data-testid="review-tab-queue"
              >
                Queue ({tasks.length})
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={cn("px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1.5",
                  activeTab === "history" ? "bg-zinc-700 text-zinc-50" : "text-zinc-500 hover:text-zinc-300"
                )}
                data-testid="review-tab-history"
              >
                <History className="h-3.5 w-3.5" />
                History
              </button>
              {(qcMode === "hard" || qcBlocked.length > 0) && (
                <button
                  onClick={() => setActiveTab("qc_blocked")}
                  className={cn("px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1.5",
                    activeTab === "qc_blocked" ? "bg-red-500/20 text-red-400" : "text-zinc-500 hover:text-zinc-300"
                  )}
                  data-testid="review-tab-qc-blocked"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  QC-blocked ({qcBlocked.length})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Queue Tab */}
        {activeTab === "queue" && (
          <>
        {/* Workflow hint */}
        <div className="p-3 bg-zinc-800/40 border border-zinc-700/50 rounded text-xs text-zinc-400 flex gap-2 mb-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <b className="text-zinc-200">Approve</b> → version is marked approved and the task moves to <b>client review</b>.
            Artist gets a notification and the task disappears from this queue.
            <span className="mx-1.5 text-zinc-600">·</span>
            <b className="text-zinc-200">Request changes</b> → status flips to <b>revision</b>, artist is notified, the task moves back to "in progress" for them.
          </div>
        </div>
        {/* Tasks List */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading...</div>
        ) : tasks.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500/50" />
              <p className="text-zinc-400">No tasks awaiting review</p>
              <p className="text-zinc-500 text-sm mt-1">All caught up</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5">
            {tasks.map((task) => {
              const latestVersion = getLatestVersion(task);

              return (
                <Card key={task.id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
                  <CardContent className="px-3 py-2.5">
                    {/* Row 1: title + actions */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-sm font-semibold text-zinc-50 truncate">{task.name}</h3>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] px-1.5 py-0 shrink-0">
                          {task.task_type || "render"}
                        </Badge>
                        {latestVersion?.ai_qc_status && latestVersion.ai_qc_status !== "skipped" && (
                          <button
                            onClick={() => setExpandedQcTaskId(expandedQcTaskId === task.id ? null : task.id)}
                            className={cn(
                              "shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border",
                              latestVersion.ai_qc_status === "passed" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                              latestVersion.ai_qc_status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/30" :
                              latestVersion.ai_qc_status === "error" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" :
                              "bg-blue-500/10 text-blue-400 border-blue-500/30"
                            )}
                            title="AI QC verdict"
                          >
                            <Brain className="h-3 w-3" />
                            {latestVersion.ai_qc_status === "passed" ? "AI: pass" :
                             latestVersion.ai_qc_status === "failed" ? "AI: fail" :
                             latestVersion.ai_qc_status === "error" ? "AI: error" :
                             `AI: ${latestVersion.ai_qc_status}`}
                            {typeof latestVersion.ai_qc_verdict?.overall_confidence === "number" &&
                              ` · ${Math.round(latestVersion.ai_qc_verdict.overall_confidence * 100)}%`}
                            {expandedQcTaskId === task.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          onClick={() => navigate(`/tasks/${task.id}`)}
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 border-zinc-700 hover:bg-zinc-800 text-xs"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          Details
                        </Button>
                        <Button
                          onClick={() => handleApprove(task.id)}
                          disabled={processing}
                          size="sm"
                          className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs"
                          title="Approve and send to client review"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleOpenRevisionDialog(task)}
                          disabled={processing}
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-xs"
                          title="Send back to the artist for changes"
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />
                          Request changes
                        </Button>
                      </div>
                    </div>

                    {/* Row 2: ALL meta in one wrapping line */}
                    <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-500 flex-wrap">
                      <span
                        className="flex items-center gap-1 hover:text-purple-400 cursor-pointer"
                        onClick={() => navigate(`/unified-projects/${task.project_id}`)}
                      >
                        <Folder className="h-3 w-3" />
                        {task.project_name || "Unknown"}
                      </span>
                      <span className="text-zinc-700">·</span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {task.assigned_artist_name || "Unassigned"}
                      </span>
                      <span className="text-zinc-700">·</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(task.updated_at)}
                      </span>
                      <span className="text-zinc-700">·</span>
                      <span className="flex items-center gap-1.5">
                        <UserCheck className="h-3 w-3 text-purple-400" />
                        {task.art_director_name ? (
                          <span className="text-purple-400">{task.art_director_name}</span>
                        ) : (
                          <Select
                            value={task.art_director_id || ""}
                            onValueChange={(value) => handleAssignArtDirector(task.id, value)}
                          >
                            <SelectTrigger className="w-36 h-5 bg-zinc-800 border-orange-500/50 text-[11px] px-2 py-0">
                              <SelectValue placeholder="Assign AD" />
                            </SelectTrigger>
                            <SelectContent>
                              {artDirectors.map((ad) => (
                                <SelectItem key={ad.id} value={ad.id}>
                                  {ad.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </span>
                      <span className="text-zinc-700">·</span>
                      {latestVersion ? (
                        <span className="flex items-center gap-1.5">
                          <span className="text-zinc-400">v{task.versions.length}</span>
                          <span className="text-zinc-600">{formatDate(latestVersion.uploaded_at)}</span>
                          {latestVersion.url && (
                            <a
                              href={latestVersion.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-0.5 text-cyan-400 hover:text-cyan-300"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Open
                            </a>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-orange-400">
                          <AlertTriangle className="h-3 w-3" />
                          No versions
                        </span>
                      )}
                    </div>

                    {/* Optional comment line */}
                    {latestVersion?.comment && (
                      <div className="mt-1.5 text-xs text-zinc-500 flex items-start gap-1.5">
                        <MessageSquare className="h-3 w-3 mt-0.5 shrink-0 text-zinc-600" />
                        <span className="line-clamp-1">{latestVersion.comment}</span>
                      </div>
                    )}

                    {/* AI QC verdict detail panel */}
                    {expandedQcTaskId === task.id && latestVersion?.ai_qc_verdict && (
                      <div className="mt-2 p-2.5 bg-zinc-800/40 border border-zinc-700/50 rounded space-y-2 text-xs">
                        <div className="text-zinc-300">
                          <Brain className="h-3 w-3 inline mr-1 text-orange-400" />
                          <b>AI verdict:</b> {latestVersion.ai_qc_verdict.overall_summary || "—"}
                        </div>
                        {Array.isArray(latestVersion.ai_qc_verdict.criteria) && latestVersion.ai_qc_verdict.criteria.length > 0 && (
                          <div className="space-y-1">
                            {latestVersion.ai_qc_verdict.criteria.map((c, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="font-mono w-32 truncate text-zinc-400">{c.key}</span>
                                <div className="flex-1 h-1.5 bg-zinc-700 rounded overflow-hidden">
                                  <div
                                    className={cn("h-full", c.pass ? "bg-green-500" : "bg-red-500")}
                                    style={{ width: `${Math.round((c.score || 0) * 100)}%` }}
                                  />
                                </div>
                                <span className="w-8 text-right text-zinc-300">{(c.score || 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {Array.isArray(latestVersion.ai_qc_verdict.issues) && latestVersion.ai_qc_verdict.issues.length > 0 && (
                          <ul className="space-y-0.5 text-zinc-400">
                            {latestVersion.ai_qc_verdict.issues.map((iss, i) => (
                              <li key={i}>
                                <span className={cn(
                                  "inline-block text-[9px] px-1 py-0.5 rounded mr-1",
                                  iss.severity === "high" ? "bg-red-600 text-white" :
                                  iss.severity === "med" ? "bg-amber-600 text-white" :
                                  "bg-zinc-600 text-zinc-50"
                                )}>{iss.severity || "—"}</span>
                                <b>{iss.area}:</b> {iss.text}
                                {iss.suggested_fix && <span className="text-zinc-500"> → {iss.suggested_fix}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="text-zinc-500 text-[10px]">
                          AI hint only — the final call is yours. Approve to send to client review or request changes from the artist.
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Revision Dialog */}
        <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">Request changes</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-zinc-400">
                Task: <span className="text-zinc-50">{selectedTask?.name}</span>
              </p>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Comment for the artist *
                </label>
                <Textarea
                  value={revisionComment}
                  onChange={(e) => setRevisionComment(e.target.value)}
                  placeholder="Describe what needs to be fixed..."
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 min-h-[100px]"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Attach file (optional)
                </label>
                <FileUploadField
                  value={revisionAttachment}
                  onChange={setRevisionAttachment}
                  placeholder="Upload a screenshot or marked-up file..."
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  maxSize={20 * 1024 * 1024}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setRevisionDialogOpen(false);
                  setRevisionAttachment("");
                }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestRevision}
                disabled={!revisionComment.trim() || processing}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Send for revision
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* QC-blocked Tab (Phase 4 — visible in hard mode or when items exist) */}
        {activeTab === "qc_blocked" && (
          <>
            <div className="p-3 bg-red-500/5 border border-red-500/20 rounded text-xs text-zinc-400 flex gap-2">
              <ShieldAlert className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                Versions auto-rejected by AI QC in <code>hard</code> mode. They don't appear in the
                regular AD queue — the task is already with the artist for revision. If the AI got it
                wrong, click "Unblock" to put the version back in the queue.
              </div>
            </div>
            {qcBlockedLoading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : qcBlocked.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-10 text-center text-zinc-500">
                  No AI-blocked versions.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-1.5">
                {qcBlocked.map((task) => {
                  const v = task.blocked_version || {};
                  const verdict = v.ai_qc_verdict || {};
                  return (
                    <Card key={task.id} className="bg-zinc-900/50 border-red-500/20">
                      <CardContent className="px-3 py-2.5 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="text-sm font-semibold text-zinc-50 truncate">{task.name}</h3>
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1.5 py-0">
                              AI blocked
                            </Badge>
                            {typeof verdict.overall_confidence === "number" && (
                              <span className="text-[10px] text-zinc-500">
                                conf: {Math.round(verdict.overall_confidence * 100)}%
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              onClick={() => navigate(`/tasks/${task.id}`)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 border-zinc-700 hover:bg-zinc-800 text-xs"
                            >
                              <Eye className="h-3.5 w-3.5 mr-1" /> Task
                            </Button>
                            <Button
                              onClick={() => handleOverrideQcBlocked(v.ai_qc_run_id)}
                              disabled={!v.ai_qc_run_id}
                              size="sm"
                              className="h-7 px-2 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                            >
                              Unblock
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Folder className="h-3 w-3" />
                            {task.project_name || "—"}
                          </span>
                          <span>·</span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {task.assigned_artist_name || "—"}
                          </span>
                          <span>·</span>
                          <span>v{v.version_number}</span>
                          <span>·</span>
                          <span>{formatDate(task.updated_at)}</span>
                        </div>
                        {verdict.overall_summary && (
                          <div className="text-xs text-zinc-400">
                            <b className="text-red-400">AI:</b> {verdict.overall_summary}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <>
            {/* AD Filter */}
            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-zinc-500" />
              <Select value={adFilter} onValueChange={setAdFilter}>
                <SelectTrigger className="w-56 bg-zinc-900 border-zinc-800 text-sm">
                  <SelectValue placeholder="All art directors" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All art directors</SelectItem>
                  {historyADs.map(ad => (
                    <SelectItem key={ad.id} value={ad.id}>{ad.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-zinc-500 text-sm">{filteredHistory.length} records</span>
            </div>

            {/* History Table */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-0">
                {historyLoading ? (
                  <div className="py-12 text-center text-zinc-500">Loading history...</div>
                ) : filteredHistory.length === 0 ? (
                  <div className="py-12 text-center text-zinc-500">No completed reviews</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-400">Task</TableHead>
                        <TableHead className="text-zinc-400">Project</TableHead>
                        <TableHead className="text-zinc-400">Art Director</TableHead>
                        <TableHead className="text-zinc-400">Artist</TableHead>
                        <TableHead className="text-zinc-400 text-center">Result</TableHead>
                        <TableHead className="text-zinc-400 text-center">Revisions</TableHead>
                        <TableHead className="text-zinc-400 text-right">Time (h)</TableHead>
                        <TableHead className="text-zinc-400">Closed at</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredHistory.map((item) => (
                        <TableRow 
                          key={item.id} 
                          className="border-zinc-800 hover:bg-zinc-800/30 cursor-pointer"
                          onClick={() => item.project_id && navigate(`/unified-projects/${item.project_id}`)}
                          data-testid={`history-row-${item.id}`}
                        >
                          <TableCell className="text-zinc-50 text-sm font-medium">{item.name}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{item.project_name}</TableCell>
                          <TableCell>
                            <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/20 text-xs">
                              {item.art_director_name || "—"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-400 text-sm">{item.assigned_artist_name || "—"}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={cn("text-xs",
                              item.review_result === "approved" ? "bg-emerald-500/20 text-emerald-400" : "bg-orange-500/20 text-orange-400"
                            )}>
                              {item.review_result === "approved" ? "Approved" : "Revision"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center text-zinc-400 text-sm">{item.revision_count || 0}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {item.duration_hours != null ? (
                              <span className={cn(
                                item.duration_hours > 48 ? "text-red-400" : item.duration_hours > 24 ? "text-amber-400" : "text-emerald-400"
                              )}>
                                {item.duration_hours}h
                              </span>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-zinc-500 text-xs">{item.completed_at?.slice(0, 10) || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ArtDirectorReviewPage;
